import axios from 'axios';
import { ActionTree } from 'vuex';
import { IDocumentEditState } from './document-edit.types';
import { EDocumentEditAction, EDocumentEditMutation } from './document-edit.constants';
import { ISender } from '@lib-shared/types/Sender';
import { IRecipient } from '@lib-shared/types/Recipient';
import { IDocument } from '@lib-shared/types/Document';
import { EnumDocumentType } from '@/enums/DocumentType';
import { DocumentService } from '@/api/documents/services/document.service';
import { translate } from '@/helpers/translation.helper';
import { ISession } from '@/types/Session';
import { VideoService } from '@/api/video/services/video.service';
import { IUser } from '@/types/User';
import { getIntegrationTemplate } from '@/helpers/signup-template.helper';
import bugsnagClient from '@lib-shared/bugsnag';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { RootState } from '@/store/store';

const documentIdFormName = 'document-id';
export const actions: ActionTree<IDocumentEditState, RootState> = {
  [EDocumentEditAction.InitializeDocumentEdit]({ commit }) {
    commit(EDocumentEditMutation.InitalizeDocumentEdit);
  },
  [EDocumentEditAction.SetRecipients]({ commit }, recipients: IRecipient[]) {
    commit(EDocumentEditMutation.SetRecipients, recipients);
  },
  async [EDocumentEditAction.SetIsSigning]({ commit }, { documentId, isSigning }) {
    commit(EDocumentEditMutation.SetIsSigning, isSigning);
    const body = new FormData();
    body.append('action', 'data');
    body.append(documentIdFormName, documentId);
    body.append('field', 'document-is_signing');
    body.append('value', isSigning ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(EDocumentEditMutation.SetIsSigning, !isSigning);
    }
  },
  async [EDocumentEditAction.LoadDocumentData]({ commit }, documentId) {
    commit(EDocumentEditMutation.LoadDocumentData, true);
    const {
      data: { document },
    } = await axios.get(`/get/document/${documentId}/details`);

    if (document) {
      const documentData: IDocument = {
        name: document.name,
        sender: document.sender,
        documentId: document.id,
        isSigning: document.is_signing,
        isSigningOrder: document.is_signing_order,
        isSelfsign: document.is_selfsign,
        type: document.type,
        videoId: document.video_id,
        isVideo: document.is_video,
      };

      const senderData: ISender = {
        email: document.sender_email,
        name: document.sender_name,
        thumbUrl: document.sender_thumb_url,
        senderId: document.user_id,
      };
      commit(EDocumentEditMutation.SetDocumentData, documentData);
      commit(EDocumentEditMutation.SetSender, senderData);
    }
    commit(EDocumentEditMutation.LoadDocumentData, false);
  },
  [EDocumentEditAction.SetNewType]({ commit }, newType: EnumDocumentType) {
    commit(EDocumentEditMutation.SetNewType, newType);
  },
  async [EDocumentEditAction.SetIsSigningOrder]({ commit, state }, { documentId, isSigningOrder }) {
    if (state.isSigningOrder === isSigningOrder) {
      return;
    }
    commit(EDocumentEditMutation.SetIsSigningOrder, isSigningOrder);
    const body = new FormData();
    body.append('action', 'data');
    body.append(documentIdFormName, documentId);
    body.append('field', 'document-is_signing_order');
    body.append('value', isSigningOrder ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(EDocumentEditMutation.SetIsSigningOrder, !isSigningOrder);
    }
  },
  async [EDocumentEditAction.SetIsSelfsign]({ commit }, { documentId, isSelfsign }) {
    commit(EDocumentEditMutation.SetIsSelfsign, isSelfsign);
    const body = new FormData();
    body.append('action', 'data');
    body.append(documentIdFormName, documentId);
    body.append('field', 'document-is_selfsign');
    body.append('value', isSelfsign ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(EDocumentEditMutation.SetIsSelfsign, !isSelfsign);
    }
  },
  async [EDocumentEditAction.SetRecipientsSigningOrders]({ commit }, { documentId, orderData }) {
    commit(EDocumentEditMutation.LoadingRecipients, true);
    const body = new FormData();
    body.append('action', 'order');
    body.append(documentIdFormName, documentId);

    orderData.forEach(data => {
      body.append('order-data[]', data);
    });

    await axios.post('/save/recipient', body);
    commit(EDocumentEditMutation.LoadingRecipients, false);
  },
  async [EDocumentEditAction.SetRecipientSigningOrder](
    { commit },
    { documentId, recipientId, orderNum }
  ) {
    commit(EDocumentEditMutation.LoadingRecipients, true);

    const body = new FormData();
    body.append('action', 'order_num');
    body.append(documentIdFormName, documentId);
    body.append('recipient-id', recipientId);
    body.append('recipient-order_num', orderNum);
    await axios.post('/save/recipient', body);
    commit(EDocumentEditMutation.LoadingRecipients, false);
  },

  async [EDocumentEditAction.RemoveVideo](
    { commit },
    { videoId, documentId }: { videoId: string; documentId: string }
  ) {
    await VideoService.removeEngageVideo(videoId, documentId);
    commit(EDocumentEditMutation.RemoveVideo);
  },

  [EDocumentEditAction.SetVideoToDocument]({ commit }, videoId: string) {
    commit(EDocumentEditMutation.SetVideo, videoId);
  },
  async [EDocumentEditAction.CreateDocument](
    { dispatch, commit },
    { session, user }: { session: ISession; user: IUser }
  ) {
    try {
      commit(EDocumentEditMutation.SetError, false);
      const templateId: string = getIntegrationTemplate(user.registrationCampaign);
      const documentId: string = await DocumentService.createSampleDocument(session, templateId);
      dispatch(EDocumentEditAction.CreateDocumentSuccess, documentId);
    } catch (e) {
      dispatch(EDocumentEditAction.CreateDocumentFailed, e);
    }
  },
  [EDocumentEditAction.CreateDocumentSuccess]({ commit }, documentId: string) {
    commit(EDocumentEditMutation.SetDocumentId, documentId);
  },
  [EDocumentEditAction.CreateDocumentFailed]({ dispatch, commit }, e: Error) {
    if (bugsnagClient) {
      bugsnagClient.notify(e);
    }
    commit(EDocumentEditMutation.SetError, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-create-document-from-template'),
      {
        root: true,
      }
    );
  },
  [EDocumentEditAction.SetActiveTab]({ commit }, tab: string) {
    commit(EDocumentEditMutation.SetActiveTab, tab);
  },
  [EDocumentEditAction.ResetError]({ commit }) {
    commit(EDocumentEditMutation.SetError, false);
  },
};
